import { navigate, PageProps } from "gatsby";
import React, { FC, useContext, useEffect } from "react";

import { useLabels } from "../../../contexts/labels";
import { PageNodesContext } from "../../../contexts/page-nodes";
import { useDataLayer } from "../../../hooks/use-data-layer";
import { usePageFullPath } from "../../../hooks/use-page-full-path";
import { GoogleAnalyticsEvent } from "../../../utils/analytics";
import { getLocalizedPath } from "../../../utils/pages";
import { removeStyleFromHtml } from "../../../utils/styles";
import { getJobTitle } from "../utils";
import { JobDetailCtas } from "./job-detail-ctas";
import { JobDetailError } from "./job-detail-error";
import { JobDetailLayout } from "./job-detail-layout";
import { JobDetailSkeleton } from "./job-detail-skeleton";
import { useGetJobDetail } from "./use-get-job-detail";

export interface JobDetailProps {
  location: PageProps["location"];
  locale: string;
}

export const JobDetail: FC<JobDetailProps> = ({ location, locale }) => {
  const { currentPage } = useContext(PageNodesContext);
  const parentPageUrl = usePageFullPath(currentPage?.parentPage?.id);
  const searchParams = new URLSearchParams(location.search);
  const jobId = searchParams.get("jobId");
  const { data: job, loading, error } = useGetJobDetail(jobId);

  const { pushEvent } = useDataLayer();
  useEffect(() => {
    if (!loading && !error && job) {
      pushEvent({
        event: GoogleAnalyticsEvent.ViewJobPosition,
        job: getJobTitle(job),
      });
    }
  }, [pushEvent, job, loading, error]);

  // Redirect to parent page if no jobId is provided
  // as user should not have been able to reach this page without
  // that query parameter
  useEffect(() => {
    if (!jobId) navigate(parentPageUrl ?? getLocalizedPath("/", locale));
  });

  const { labels } = useLabels();

  if (loading) {
    return <JobDetailSkeleton labels={labels} />;
  }

  if (error) {
    return <JobDetailError labels={labels} error={error} />;
  }

  if (!job || !jobId) {
    return <JobDetailError labels={labels} error={labels["job-not-found"] ?? ""} />;
  }

  const {
    jobReqLocale: {
      results: { jobDescription, externalJobDescription, intJobDescHeader, extJobDescHeader },
    },
  } = job;

  const descriptionHtml = removeStyleFromHtml(intJobDescHeader ?? extJobDescHeader ?? "");
  const jobDescriptionHtml = removeStyleFromHtml(jobDescription ?? externalJobDescription ?? "");

  return (
    <JobDetailLayout
      labels={labels}
      title={getJobTitle(job)}
      description={<div dangerouslySetInnerHTML={{ __html: descriptionHtml }} />}
      jobDescription={<div dangerouslySetInnerHTML={{ __html: jobDescriptionHtml }} />}
      ctas={<JobDetailCtas job={job} labels={labels} />}
    />
  );
};
